import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ClipLoader } from 'react-spinners';
import { toastr } from 'react-redux-toastr';
import moment from 'moment';
import { fetchScheduleCalendar } from 'state/actions/schedule';
import './ScreenCalendar.scss';

const MIN_LANE_HEIGHT = 80;
const WIN_START = 0;
const WIN_END = 24 * 60;
const WIN_WIDTH = WIN_END - WIN_START;
const HOUR_STEP = 1;

function ScreenCalendar() {
  const { activeScreen } = useSelector((state) => ({
    activeScreen: state.screens.activeScreen,
  }));

  const screenId = activeScreen?._id;

  const [date, setDate] = useState(() => moment().format('YYYY-MM-DD'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!screenId) return;
    let cancelled = false;
    setLoading(true);
    fetchScheduleCalendar({ screenId, date })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) {
          setData(null);
          toastr.error('', err?.response?.data?.message || 'Failed to load calendar');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [screenId, date]);

  const goPrev = () => setDate(moment(date).subtract(1, 'day').format('YYYY-MM-DD'));
  const goNext = () => setDate(moment(date).add(1, 'day').format('YYYY-MM-DD'));
  const goToday = () => setDate(moment().format('YYYY-MM-DD'));

  // One schedule per row. The API packs schedules that don't overlap in time
  // onto a shared row, which makes two unrelated bookings read as one track —
  // so `bar.row` and `data.rowCount` are recomputed here, earliest start first.
  const bars = useMemo(() => {
    const list = [...(data?.schedules || [])];
    list.sort(
      (a, b) =>
        (a.startMinutes || 0) - (b.startMinutes || 0) ||
        (a.endMinutes || 0) - (b.endMinutes || 0) ||
        String(a.name || '').localeCompare(String(b.name || ''))
    );
    return list.map((bar, row) => ({ ...bar, row }));
  }, [data]);

  const rowCount = Math.max(bars.length, 1);
  const isToday = date === moment().format('YYYY-MM-DD');

  const LANE_HEIGHT = MIN_LANE_HEIGHT;

  const nowLeftPct = useMemo(() => {
    if (!isToday) return null;
    const now = new Date();
    const m = now.getHours() * 60 + now.getMinutes();
    if (m < WIN_START || m > WIN_END) return null;
    return ((m - WIN_START) / WIN_WIDTH) * 100;
  }, [isToday, date]);

  const hours = [];
  for (let h = 0; h <= 24; h += HOUR_STEP) {
    hours.push(h);
  }

  return (
    <div className="screen-calendar">
      <div className="screen-calendar__toolbar">
        <button className="button-secondary" onClick={goPrev} type="button">
          <i className="feather-chevron-left" />
        </button>
        <button className="button-secondary" onClick={goToday} type="button">
          Today
        </button>
        <button className="button-secondary" onClick={goNext} type="button">
          <i className="feather-chevron-right" />
        </button>
        <input
          type="date"
          className="screen-calendar__date-input"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        <span className="screen-calendar__date-label">
          {moment(date).format('dddd, DD MMM YYYY')}
        </span>
      </div>

      {loading ? (
        <div className="screen-calendar__loading">
          <ClipLoader />
        </div>
      ) : (
        <div className="screen-calendar__viewport">
          <div className="screen-calendar__hours">
            {hours.map((h) => {
              const left = ((h * 60 - WIN_START) / WIN_WIDTH) * 100;
              return (
                <div
                  key={h}
                  className="screen-calendar__hour-label"
                  style={{ left: `${left}%` }}
                >
                  {String(h).padStart(2, '0')}:00
                </div>
              );
            })}
          </div>

          <div className="screen-calendar__grid-wrap">
            {hours.map((h) => {
              const left = ((h * 60 - WIN_START) / WIN_WIDTH) * 100;
              return (
                <div
                  key={`line-${h}`}
                  className="screen-calendar__gridline"
                  style={{ left: `${left}%` }}
                />
              );
            })}
            {nowLeftPct !== null && (
              <div
                className="screen-calendar__now"
                style={{ left: `${nowLeftPct}%` }}
              />
            )}
          <div
            className="screen-calendar__grid"
            style={{ height: rowCount * LANE_HEIGHT }}
          >
            {Array.from({ length: rowCount }).map((_, r) => (
              <div
                key={`lane-${r}`}
                className="screen-calendar__lane"
                style={{ top: r * LANE_HEIGHT, height: LANE_HEIGHT }}
              />
            ))}

            {bars.map((bar) => {
              const rawLeft =
                ((bar.startMinutes - WIN_START) / WIN_WIDTH) * 100;
              const rawWidth =
                ((bar.endMinutes - bar.startMinutes) / WIN_WIDTH) * 100;
              const left = Math.max(0, rawLeft);
              const width = Math.max(
                0,
                Math.min(100 - left, rawWidth - (left - rawLeft))
              );
              return (
                <div
                  key={bar.scheduleId}
                  className="screen-calendar__bar"
                  style={{
                    left: `${left}%`,
                    width: `${width}%`,
                    top: bar.row * LANE_HEIGHT + 4,
                    height: LANE_HEIGHT - 8,
                    background: `${bar.color}22`,
                    borderLeft: `3px solid ${bar.color}`,
                  }}
                  title={`${bar.name} ${bar.start}–${bar.end}`}
                >
                  <div className="screen-calendar__bar-title">{bar.name}</div>
                  <div className="screen-calendar__bar-sub">
                    {bar.start} – {bar.end}
                  </div>
                  {bar.targetImpression > 0 && (
                    <span className="screen-calendar__bar-badge">
                      {bar.targetImpression} {bar.impressionType}
                    </span>
                  )}
                </div>
              );
            })}

          </div>
          </div>

          {data && bars.length === 0 && (
            <div className="screen-calendar__empty">
              No schedules on this day.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ScreenCalendar;
